import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { FadeUp, MaskedLines } from "@/components/Reveal";

const API = process.env.REACT_APP_BACKEND_URL;
const resolveSrc = (u) => (u && u.startsWith("/api/") ? `${API}${u}` : u);
const shapeClass = (shape) =>
  shape === "tall"
    ? "row-span-2 rounded-t-[999px]"
    : shape === "wide"
    ? "col-span-2 rounded-[2rem]"
    : "rounded-[2rem]";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const isOpen = openIndex !== null;
  const active = isOpen ? items[openIndex] : null;

  useEffect(() => {
    axios
      .get(`${API}/api/gallery`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  const go = (dir) => setOpenIndex((i) => (i + dir + items.length) % items.length);
  const close = () => setOpenIndex(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative bg-ivory py-32 sm:py-44">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <FadeUp className="mb-6">
          <p className="text-[12px] tracking-[0.45em] uppercase text-coral">The Portfolio</p>
        </FadeUp>
        <h2 className="font-serif font-light text-ink leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-20 sm:mb-28">
          <MaskedLines inView lines={[<>A gallery of <span className="italic text-coral">happily-ever-afters</span></>]} />
        </h2>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 auto-rows-[180px] sm:auto-rows-[240px]">
          {items.map((item, i) => (
            <FadeUp
              key={item.id}
              delay={i * 0.06}
              onClick={() => setOpenIndex(i)}
              data-testid={`gallery-item-${i}`}
              className={`group relative overflow-hidden spotlight-frame cursor-pointer ${shapeClass(item.shape)}`}
            >
              <motion.img
                src={resolveSrc(item.image_url)}
                alt={item.description}
                loading="lazy"
                className="w-full h-full object-cover"
                whileHover={{ scale: 1.07 }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-midnight/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              {item.description && (
                <p className="absolute bottom-5 left-6 right-6 font-quote italic text-ivory text-lg opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-500">
                  {item.description}
                </p>
              )}
            </FadeUp>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {isOpen && active && (
          <motion.div
            key="gallery-lightbox"
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[200] flex items-center justify-center bg-midnight/90 backdrop-blur-xl px-4 sm:px-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={close}
          >
            <button
              data-testid="lightbox-close"
              onClick={(e) => { e.stopPropagation(); close(); }}
              aria-label="Close"
              className="absolute top-6 right-6 w-12 h-12 rounded-full bg-ivory/10 hover:bg-coral text-ivory flex items-center justify-center transition-colors duration-300"
            >
              <X size={20} strokeWidth={1.5} />
            </button>

            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); go(-1); }}
              aria-label="Previous photo"
              className="absolute left-4 sm:left-8 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-ivory/10 hover:bg-coral text-ivory flex items-center justify-center transition-colors duration-300 z-10"
            >
              <ChevronLeft size={24} strokeWidth={1.5} />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); go(1); }}
              aria-label="Next photo"
              className="absolute right-4 sm:right-8 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-ivory/10 hover:bg-coral text-ivory flex items-center justify-center transition-colors duration-300 z-10"
            >
              <ChevronRight size={24} strokeWidth={1.5} />
            </button>

            <div className="relative max-w-[1100px] w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
              <AnimatePresence mode="wait">
                <motion.img
                  key={openIndex}
                  data-testid="lightbox-image"
                  src={resolveSrc(active.image_url)}
                  alt={active.description}
                  className="max-h-[78vh] w-auto max-w-full object-contain rounded-[1.5rem] shadow-[0_40px_120px_-30px_rgba(0,0,0,0.8)]"
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                />
              </AnimatePresence>
              <div className="mt-6 flex items-center gap-4">
                {active.description && (
                  <p data-testid="lightbox-caption" className="font-quote italic text-ivory text-lg sm:text-xl">
                    {active.description}
                  </p>
                )}
                <span className="text-ivory/50 text-[12px] tracking-[0.25em] uppercase">
                  {openIndex + 1} / {items.length}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
